/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineViewBoards, HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import type { Action, Column } from "../../ui";
import { useBannerContext } from "../../context/BannerContext";
import { pushOneBanner } from "../../lib/sysNavigation/SysNavigation";
import { _PAGE_SIZE_ } from "../../lib/other/tools";

type Banner = {
  banner_id: number | string;
  title?: string;
  description?: string;
  thumbnail?: string;
  [key: string]: unknown;
};

const addRoute = "/banner/add";
const editRoute = "/banner/edit";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { banner, count, fetchBanners, deleteBanner, updateBannerState } =
    useBannerContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!banner) fetchBanners(page, pageSize);
  }, [banner]);

  const rows = useMemo<Banner[] | null>(() => {
    if (!Array.isArray(banner)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return banner as Banner[];
    return (banner as Banner[]).filter(
      (b) =>
        String(b.title ?? "").toLowerCase().includes(q) ||
        String(b.description ?? "").toLowerCase().includes(q),
    );
  }, [banner, query]);

  const columns: Column<Banner>[] = [
    {
      header: "Title",
      cell: (row) => (
        <div className="flex items-center gap-3">
          {row.thumbnail ? (
            <img
              src={row.thumbnail}
              alt={row.title ?? ""}
              className="h-10 w-16 rounded-md object-cover ring-1 ring-gray-200"
            />
          ) : (
            <span className="flex h-10 w-16 items-center justify-center rounded-md bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
              <HiOutlineViewBoards className="h-4 w-4" />
            </span>
          )}
          <div className="min-w-0">
            <p className="truncate font-medium text-gray-900">{row.title ?? "—"}</p>
            <p className="truncate text-[11.5px] text-gray-500">
              {row.description ?? ""}
            </p>
          </div>
        </div>
      ),
    },
  ];

  const handleDelete = async (row: Banner) => {
    const response: any = await deleteBanner(row.banner_id);
    if (response?.status === 200 && Array.isArray(banner)) {
      updateBannerState(
        (banner as Banner[]).filter((b) => b.banner_id !== row.banner_id),
      );
    }
  };

  const rowActions = (row: Banner): Action[] => [
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOneBanner(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Delete ${row.title ?? "this banner"}?`,
        description: "Visitors will no longer see this banner on the site.",
        confirmLabel: "Delete",
      },
    },
  ];

  return (
    <ListPage<Banner>
      title="Banners"
      subtitle="Promotional banners shown on the public site."
      icon={<HiOutlineViewBoards className="h-5 w-5" />}
      actions={[{ label: "Add banner", variant: "primary", href: addRoute }]}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.banner_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search banners…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchBanners(p, ps);
        },
      }}
      empty="No banners yet."
    />
  );
};

export default Overview;
